/**
 * Transaction Rule Context for managing transaction rule state across the application
 * Provides centralized transaction rule management with proper error handling
 */
import React, { createContext, useContext, useEffect, useCallback, ReactNode, useState, useRef } from 'react';
import { TransactionRule, CreateTransactionRuleData, UpdateTransactionRuleData, RuleMatchingRequest, RuleMatchingResponse, TransactionRuleFilters } from '../types/transactionRule';
import { useAsyncList } from '../hooks/useAsyncState';
import transactionRuleService from '../services/transactionRuleService';
import { useOrganization } from './OrganizationContext';

interface TransactionRuleContextType {
  // State
  rules: TransactionRule[];
  loading: boolean;
  error: string | null;
  lastSyncTimestamp: Date | null;
  
  // Actions
  loadRules: () => Promise<TransactionRule[]>;
  createRule: (data: CreateTransactionRuleData) => Promise<TransactionRule>;
  updateRule: (data: UpdateTransactionRuleData) => Promise<TransactionRule>;
  deleteRule: (id: string) => Promise<void>;
  deleteRules: (ids: string[]) => Promise<void>;
  createBulkRules: (data: CreateTransactionRuleData[]) => Promise<any>;
  bulkUpdatePriorities: (priorityUpdates: Array<{id: string, priority: number}>) => Promise<void>;
  matchRule: (request: RuleMatchingRequest) => Promise<RuleMatchingResponse>;
  searchRules: (term: string) => Promise<TransactionRule[]>;
  getRuleById: (id: string) => TransactionRule | null;
  getActiveRules: () => Promise<TransactionRule[]>;
  refreshRules: () => Promise<void>;
  
  // Utility functions
  reset: () => void;
}

const TransactionRuleContext = createContext<TransactionRuleContextType | undefined>(undefined);

interface TransactionRuleProviderProps {
  children: ReactNode;
}

export const TransactionRuleProvider: React.FC<TransactionRuleProviderProps> = ({ children }) => {
  const { organization } = useOrganization();
  const previousOrgIdRef = useRef<string | null>(null);

  // Stable async function for transaction rules
  const getTransactionRules = useCallback(
    () => transactionRuleService.getAll(),
    []
  );

  const {
    state: { data: rules, loading, error },
    fetchItems: fetchRulesInternal,
    reset,
    setData,
    addItem,
    updateItem,
    removeItem,
    removeItems
  } = useAsyncList<TransactionRule>(getTransactionRules, []);

  // Track last sync timestamp
  const [lastSyncTimestamp, setLastSyncTimestamp] = useState<Date | null>(
    transactionRuleService.getLastSyncTimestamp()
  );

  // Wrapper to return the rules array
  const loadRules = useCallback(async (): Promise<TransactionRule[]> => {
    const result = await fetchRulesInternal();
    setLastSyncTimestamp(transactionRuleService.getLastSyncTimestamp());
    return result;
  }, [fetchRulesInternal]);

  // Load rules on mount and clear legacy storage
  useEffect(() => {
    let mounted = true;
    
    const loadInitialRules = async () => {
      try {
        
        
        await fetchRulesInternal();
        if (mounted) {
          setLastSyncTimestamp(transactionRuleService.getLastSyncTimestamp());
        }
      } catch (error) {
        if (mounted) {
          console.error('Failed to load transaction rules on mount:', error);
        }
      }
    };

    loadInitialRules();

    return () => {
      mounted = false;
    };
  }, [fetchRulesInternal]); // Include fetchRulesInternal dependency

  // Background sync - validate cache periodically
  useEffect(() => {
    let mounted = true;
    
    const backgroundSync = async () => {
      try {
        const validation = await transactionRuleService.validateCache();
        if (!validation.isValid && mounted) {
          console.warn('Background sync detected cache issues:', validation.issues);
          
          // Auto-refresh if we detect phantom rules or major inconsistencies
          const hasPhantomRules = validation.issues.some(issue => 
            issue.includes('phantom rules') || issue.includes('count mismatch')
          );
          
          if (hasPhantomRules) {
            console.log('Auto-refreshing due to phantom rules...');
            const refreshedRules = await transactionRuleService.forceRefresh();
            if (mounted) {
              setData(refreshedRules);
              setLastSyncTimestamp(transactionRuleService.getLastSyncTimestamp());
            }
          }
        }
      } catch (error) {
        // Silently log background sync errors to avoid spamming console
        if (mounted) {
          console.debug('Background sync failed:', error);
        }
      }
    };

    // Run background sync every 20 minutes
    const interval = setInterval(backgroundSync, 1200000);
    
    // Run initial background sync after 20 seconds
    const timeout = setTimeout(backgroundSync, 20000);

    return () => {
      mounted = false;
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, [setData]);

  // Detect organization changes and reload data
  useEffect(() => {
    const currentOrgId = organization?.id || null;
    const previousOrgId = previousOrgIdRef.current;

    // If organization changed (and it's not the initial load)
    if (previousOrgId !== null && previousOrgId !== currentOrgId) {
      console.log('[TransactionRuleContext] Organization changed, reloading transaction rules');
      
      // Reset state and reload data
      reset();
      
      // Load fresh data for the new organization
      if (currentOrgId) {
        fetchRulesInternal().then(() => {
          setLastSyncTimestamp(transactionRuleService.getLastSyncTimestamp());
        }).catch(error => {
          console.error('[TransactionRuleContext] Failed to reload rules after org switch:', error);
        });
      }
    }

    // Update the ref for next comparison
    previousOrgIdRef.current = currentOrgId;
  }, [organization?.id, reset, fetchRulesInternal]);

  // Create new rule
  const createRule = async (data: CreateTransactionRuleData): Promise<TransactionRule> => {
    try {
      const newRule = await transactionRuleService.create(data);
      addItem(newRule);
      return newRule;
    } catch (error) {
      console.error('Failed to create transaction rule:', error);
      throw error;
    }
  };

  // Update existing rule
  const updateRule = async (data: UpdateTransactionRuleData): Promise<TransactionRule> => {
    try {
      const updatedRule = await transactionRuleService.update(data);
      updateItem(updatedRule, (rule) => rule.id);
      return updatedRule;
    } catch (error) {
      console.error('Failed to update transaction rule:', error);
      throw error;
    }
  };

  // Delete single rule
  const deleteRule = async (id: string): Promise<void> => {
    try {
      await transactionRuleService.delete(id);
      removeItem(id, (rule) => rule.id);
    } catch (error: any) {
      console.error('Failed to delete transaction rule:', error);
      
      // If it's a stale data error, refresh the rule list
      if (error?.code === 'STALE_DATA') {
        console.log('Refreshing rule list due to stale data...');
        const refreshedRules = await transactionRuleService.getAll();
        setData(refreshedRules);
        setLastSyncTimestamp(transactionRuleService.getLastSyncTimestamp());
      }
      
      throw error;
    }
  };

  // Delete multiple rules
  const deleteRules = async (ids: string[]): Promise<void> => {
    try {
      await transactionRuleService.deleteBulk(ids);
      removeItems(ids, (rule) => rule.id);
    } catch (error) {
      console.error('Failed to delete transaction rules:', error);
      throw error;
    }
  };

  // Create multiple rules
  const createBulkRules = async (data: CreateTransactionRuleData[]): Promise<any> => {
    try {
      const result = await transactionRuleService.createBulk(data);
      
      // Add successfully created rules to state
      if (result.created && result.created.length > 0) {
        const currentRules = rules || [];
        setData([...result.created, ...currentRules]);
      }
      
      return result;
    } catch (error) {
      console.error('Failed to create bulk transaction rules:', error);
      throw error;
    }
  };

  // Bulk update rule priorities
  const bulkUpdatePriorities = async (priorityUpdates: Array<{id: string, priority: number}>): Promise<void> => {
    try {
      await transactionRuleService.bulkUpdatePriorities(priorityUpdates);
      
      // Update local state optimistically
      const priorityMap = new Map(priorityUpdates.map(update => [update.id, update.priority]));
      const updatedRules = (rules || []).map(rule => {
        const newPriority = priorityMap.get(rule.id);
        return newPriority !== undefined ? { ...rule, priority: newPriority } : rule;
      });
      setData(updatedRules);
    } catch (error) {
      console.error('Failed to update rule priorities:', error);
      throw error;
    }
  };

  // Match rule against transaction
  const matchRule = async (request: RuleMatchingRequest): Promise<RuleMatchingResponse> => {
    try {
      return await transactionRuleService.matchRule(request);
    } catch (error) {
      console.error('Failed to match transaction rule:', error);
      throw error;
    }
  };

  // Search rules
  const searchRules = async (term: string): Promise<TransactionRule[]> => {
    try {
      return await transactionRuleService.search(term);
    } catch (error) {
      console.error('Failed to search transaction rules:', error);
      throw error;
    }
  };

  // Get rule by ID (from current state)
  const getRuleById = (id: string): TransactionRule | null => {
    return rules?.find(rule => rule.id === id) || null;
  };

  // Get active rules
  const getActiveRules = async (): Promise<TransactionRule[]> => {
    try {
      return await transactionRuleService.getActive();
    } catch (error) {
      console.error('Failed to get active transaction rules:', error);
      throw error;
    }
  };

  // Refresh rules from server
  const refreshRules = async (): Promise<void> => {
    try {
      const refreshedRules = await transactionRuleService.refresh();
      setData(refreshedRules);
      setLastSyncTimestamp(transactionRuleService.getLastSyncTimestamp());
    } catch (error) {
      console.error('Failed to refresh transaction rules:', error);
      throw error;
    }
  };

  const contextValue: TransactionRuleContextType = {
    // State
    rules: rules || [],
    loading,
    error,
    lastSyncTimestamp,
    
    // Actions
    loadRules,
    createRule,
    updateRule,
    deleteRule,
    deleteRules,
    createBulkRules,
    bulkUpdatePriorities,
    matchRule,
    searchRules,
    getRuleById,
    getActiveRules,
    refreshRules,
    
    // Utility
    reset
  };

  return (
    <TransactionRuleContext.Provider value={contextValue}>
      {children}
    </TransactionRuleContext.Provider>
  );
};

/**
 * Hook to use Transaction Rule context
 * Throws error if used outside of TransactionRuleProvider
 */
export const useTransactionRule = (): TransactionRuleContextType => {
  const context = useContext(TransactionRuleContext);
  if (context === undefined) {
    throw new Error('useTransactionRule must be used within a TransactionRuleProvider');
  }
  return context;
};

/**
 * Hook for transaction rule operations with optimistic updates and error handling
 */
export const useTransactionRuleOperations = () => {
  const context = useTransactionRule();
  
  const createWithValidation = async (data: CreateTransactionRuleData) => {
    // Basic validation
    if (!data.description?.trim()) {
      throw new Error('Rule description is required');
    }
    if (!data.naturalLanguageRule?.trim()) {
      throw new Error('Rule condition is required');
    }
    if (!data.glAccountNumber?.trim()) {
      throw new Error('GL account is required');
    }
    
    return context.createRule(data);
  };
  
  const updateWithValidation = async (data: UpdateTransactionRuleData) => {
    if (!data.id) {
      throw new Error('Rule ID is required for update');
    }
    
    return context.updateRule(data);
  };
  
  return {
    ...context,
    createRule: createWithValidation,
    updateRule: updateWithValidation
  };
};

export default TransactionRuleContext;